import { apiFetchData, ApiError, withAuthHeader, type AuthRetry } from '@/shared/lib/api';
import { useAuthStore } from '@/features/auth/store';
import type {
  AdvanceTargetStatus,
  OrderActionErrorCode,
  OrderResponse,
  OrderStatus,
  OrderTypeEnum,
  UpdateScheduledTimeRequest,
} from './types';

const OWNER_BASE = '/api/owner/restaurants';

function getAuthRetry(): AuthRetry {
  return {
    getToken: () => useAuthStore.getState().token,
    refreshTokens: () => useAuthStore.getState().refreshTokens(),
  };
}

function getToken(): string {
  const t = useAuthStore.getState().token;
  if (!t) throw new Error('No token');
  return t;
}

/**
 * Typed error thrown by accept/reject/advance. Exposes the backend's 409
 * `error` discriminator so callers can branch on `payment_capture_failed`
 * vs `Conflict` without rummaging through `ApiError.details`.
 */
export class OrderActionError extends Error {
  status: number;
  code: OrderActionErrorCode;

  constructor(message: string, status: number, code: OrderActionErrorCode) {
    super(message);
    this.name = 'OrderActionError';
    this.status = status;
    this.code = code;
  }
}

function rethrowAsOrderActionError(e: unknown): never {
  if (e instanceof ApiError) {
    const details = e.details as { error?: string; message?: string } | null | undefined;
    const raw = details?.error;
    const code: OrderActionErrorCode =
      raw === 'payment_capture_failed' || raw === 'Conflict' ? raw : 'unknown';
    throw new OrderActionError(details?.message ?? e.message, e.status, code);
  }
  throw e;
}

/** Date-range params for the owner orders list. Both bounds are inclusive. */
export interface OrdersListParams {
  /** YYYY-MM-DD, inclusive start of the window. */
  from?: string;
  /** YYYY-MM-DD, inclusive end of the window. */
  to?: string;
  /** When true, filters to SCHEDULED orders only. Backend defaults lower bound to today. */
  scheduled?: boolean;
}

function buildOrdersUrl(restaurantId: string, params?: OrdersListParams): string {
  const base = `${OWNER_BASE}/${restaurantId}/orders`;
  if (!params) return base;
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.scheduled) qs.set('scheduled', 'true');
  const s = qs.toString();
  return s ? `${base}?${s}` : base;
}

export const ordersApi = {
  async getOrders(
    restaurantId: string,
    params?: OrdersListParams,
  ): Promise<OrderResponse[]> {
    const token = getToken();
    const list = await apiFetchData<OrderResponse[]>(
      buildOrdersUrl(restaurantId, params),
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
    return Array.isArray(list) ? list : [];
  },

  async getOrdersByStatus(restaurantId: string, status: OrderStatus): Promise<OrderResponse[]> {
    const token = getToken();
    const list = await apiFetchData<OrderResponse[]>(
      `${OWNER_BASE}/${restaurantId}/orders/status/${status}`,
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
    return Array.isArray(list) ? list : [];
  },

  async getOrdersByType(restaurantId: string, type: OrderTypeEnum): Promise<OrderResponse[]> {
    const token = getToken();
    const list = await apiFetchData<OrderResponse[]>(
      `${OWNER_BASE}/${restaurantId}/orders/type/${type}`,
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
    return Array.isArray(list) ? list : [];
  },

  async getOrder(restaurantId: string, orderId: string): Promise<OrderResponse> {
    const token = getToken();
    return apiFetchData<OrderResponse>(
      `${OWNER_BASE}/${restaurantId}/orders/${orderId}`,
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  async acceptOrder(restaurantId: string, orderId: string): Promise<OrderResponse> {
    const token = getToken();
    try {
      return await apiFetchData<OrderResponse>(
        `${OWNER_BASE}/${restaurantId}/orders/${orderId}/accept`,
        { method: 'POST', headers: withAuthHeader(undefined, token) },
        getAuthRetry()
      );
    } catch (e) {
      rethrowAsOrderActionError(e);
    }
  },

  async rejectOrder(
    restaurantId: string,
    orderId: string,
    reason?: string
  ): Promise<OrderResponse> {
    const token = getToken();
    const body = reason && reason.trim().length > 0 ? { reason: reason.trim() } : undefined;
    try {
      return await apiFetchData<OrderResponse>(
        `${OWNER_BASE}/${restaurantId}/orders/${orderId}/reject`,
        { method: 'POST', headers: withAuthHeader(undefined, token), body },
        getAuthRetry()
      );
    } catch (e) {
      rethrowAsOrderActionError(e);
    }
  },

  async advanceOrder(
    restaurantId: string,
    orderId: string,
    targetStatus: AdvanceTargetStatus
  ): Promise<OrderResponse> {
    const token = getToken();
    try {
      return await apiFetchData<OrderResponse>(
        `${OWNER_BASE}/${restaurantId}/orders/${orderId}/advance`,
        { method: 'POST', headers: withAuthHeader(undefined, token), body: { targetStatus } },
        getAuthRetry()
      );
    } catch (e) {
      rethrowAsOrderActionError(e);
    }
  },

  async refundOrder(
    restaurantId: string,
    orderId: string,
    body?: { amount?: number; reason?: string },
  ): Promise<OrderResponse> {
    const token = getToken();
    return apiFetchData<OrderResponse>(
      `${OWNER_BASE}/${restaurantId}/orders/${orderId}/refund`,
      { method: 'POST', headers: withAuthHeader(undefined, token), body },
      getAuthRetry()
    );
  },

  async getScheduledOrders(restaurantId: string): Promise<OrderResponse[]> {
    const token = getToken();
    const list = await apiFetchData<OrderResponse[]>(
      buildOrdersUrl(restaurantId, { scheduled: true }),
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
    return Array.isArray(list) ? list : [];
  },

  async startPreparing(restaurantId: string, orderId: string): Promise<OrderResponse> {
    const token = getToken();
    try {
      return await apiFetchData<OrderResponse>(
        `${OWNER_BASE}/${restaurantId}/orders/${orderId}/start-preparing`,
        { method: 'POST', headers: withAuthHeader(undefined, token) },
        getAuthRetry()
      );
    } catch (e) {
      rethrowAsOrderActionError(e);
    }
  },

  async updateScheduledTime(
    restaurantId: string,
    orderId: string,
    body: UpdateScheduledTimeRequest
  ): Promise<OrderResponse> {
    const token = getToken();
    return apiFetchData<OrderResponse>(
      `${OWNER_BASE}/${restaurantId}/orders/${orderId}/scheduled-time`,
      { method: 'PATCH', headers: withAuthHeader(undefined, token), body },
      getAuthRetry()
    );
  },

  /**
   * @deprecated Backend deprecated `PUT /status` in favor of `/accept`, `/reject`, `/advance`.
   * Retained while the legacy UI still calls it; migrate call sites to the typed methods above.
   */
  async updateOrderStatus(
    restaurantId: string,
    orderId: string,
    status: string
  ): Promise<OrderResponse> {
    const token = getToken();
    return apiFetchData<OrderResponse>(
      `${OWNER_BASE}/${restaurantId}/orders/${orderId}/status`,
      { method: 'PUT', headers: withAuthHeader(undefined, token), body: { status } },
      getAuthRetry()
    );
  },
};
